import React, { useMemo } from "react"
import { useParams } from "react-router-dom"
import { Loader } from "@mantine/core"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CampaignSubpageHeader } from "QuorumGrassroots/widgets/ReusableComponents/CampaignSubpageHeader"
import {
    SubpageContainer,
    LoadingContainer,
    NextElectionBannerWrapper,
    Wrapper,
    EmptyStateContainer,
    EmptyStateIconWrapper,
    EmptyStateTitle,
    EmptyStateDescription,
} from "QuorumGrassroots/pages/campaign/key-dates/KeyDatesPage.styles"
import { NextElectionInfo } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/NextElectionInfo/NextElectionInfo"
import { useCampaign } from "QuorumGrassroots/services/grassrootsCampaign"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import { CollapsibleKeyDatesSection } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CollapsibleKeyDatesSection/CollapsibleKeyDatesSection"
import { useElectionCountdown } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/hooks/useElectionCountdown"
import {
    extractRegistrationDates,
    extractAbsenteeBallotDates,
    extractEarlyVotingDates,
    createCalendarEvent,
    getStateFromElection,
    formatDate,
} from "QuorumGrassroots/pages/campaign/key-dates/KeyDatesPage.helper"

export const KeyDatesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()

    const { data: campaign, isLoading: isCampaignLoading, isError: isCampaignError } = useCampaign(id)
    const { data: electionData, isLoading: isElectionLoading, isError: isElectionError } = useElectionCountdown(id)

    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign?.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign?.social_share_text ?? `Help ${campaign?.name} get out the vote!`,
        imageUrl: campaign?.social_image_url,
    })

    // Extract key dates from election data
    const { registrationDates, absenteeBallotDates, earlyVotingDates, nextElection, calendarEvent } = useMemo(() => {
        const firstElection = electionData?.elections?.[0]

        if (!firstElection) {
            return {
                registrationDates: [],
                absenteeBallotDates: [],
                earlyVotingDates: [],
                nextElection: null,
                calendarEvent: undefined,
            }
        }

        return {
            registrationDates: extractRegistrationDates(firstElection.registration),
            absenteeBallotDates: extractAbsenteeBallotDates(firstElection.voting),
            earlyVotingDates: extractEarlyVotingDates(firstElection.voting),
            nextElection: firstElection,
            calendarEvent: createCalendarEvent(firstElection),
        }
    }, [electionData])

    // Format election name and date for display
    const electionName = useMemo(() => {
        if (!nextElection) return ""
        const state = getStateFromElection(nextElection)
        return `${state} Election`
    }, [nextElection])

    const electionDate = useMemo(() => {
        if (!nextElection?.date) return ""
        try {
            return formatDate(nextElection.date)
        } catch {
            return nextElection.date
        }
    }, [nextElection])

    const isLoading = isCampaignLoading || isElectionLoading

    if (isLoading)
        return (
            <Wrapper>
                <LoadingContainer>
                    <Loader size="sm" /> Loading...
                </LoadingContainer>
            </Wrapper>
        )
    if (isCampaignError)
        return (
            <Wrapper>
                <SubpageContainer>
                    <p>Error loading campaign. Please try again later.</p>
                </SubpageContainer>
            </Wrapper>
        )

    if (isElectionError) {
        return (
            <Wrapper>
                <SubpageContainer>
                    <p>Error loading elections. Please try again later.</p>
                </SubpageContainer>
            </Wrapper>
        )
    }

    return (
        <SubpageContainer>
            <CampaignTitleSubtitle campaign={campaign} />
            <CampaignSubpageHeader title="Check Key Dates" backUrl={`/campaign/${id}`} dataCy="key-dates-header" />

            {/* Next Election banner with calendar integration */}
            {nextElection && (
                <NextElectionBannerWrapper>
                    <NextElectionInfo
                        electionName={electionName}
                        electionDate={electionDate}
                        calendarEvent={calendarEvent}
                    />
                </NextElectionBannerWrapper>
            )}

            {registrationDates.length > 0 && (
                <CollapsibleKeyDatesSection title="Last day to register" dates={registrationDates} />
            )}

            {absenteeBallotDates.length > 0 && (
                <CollapsibleKeyDatesSection title="Request an absentee ballot" dates={absenteeBallotDates} />
            )}

            {earlyVotingDates.length > 0 && (
                <CollapsibleKeyDatesSection title="Early voting" dates={earlyVotingDates} />
            )}

            {!nextElection && (
                <EmptyStateContainer>
                    <EmptyStateIconWrapper>
                        <i className="fa fa-calendar-times-o" aria-hidden="true" />
                    </EmptyStateIconWrapper>
                    <EmptyStateTitle>No dates available</EmptyStateTitle>
                    <EmptyStateDescription>No more dates were found for this election.</EmptyStateDescription>
                </EmptyStateContainer>
            )}

            <GotvCampaignFooter
                socialPlatforms={socialPlatforms}
                socialHeading={campaign.social_header ?? DEFAULT_SOCIAL_HEADER}
            />
        </SubpageContainer>
    )
}
